import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import {
  Briefcase,
  Building2,
  Check,
  ChevronsUpDown,
  Flame,
  Loader2,
  Sparkles,
  User,
} from 'lucide-react';

/**
 * Searchable jobs combobox — replaces both the legacy left "Open Jobs" panel and
 * the simple Select dropdown. Type to filter, arrow to navigate, Enter to pick.
 * "Best 10" AI shortlist is preserved as an inline action on every job row.
 */
const JobsCombobox = ({
  jobs,
  value,
  onChange,
  appCountByJob = {},
  onBest10,
  loading = false,
}) => {
  const [open, setOpen] = useState(false);
  const selected = jobs.find((j) => j.id === value);
  const triggerLabel = !value || value === 'all'
    ? 'All Jobs'
    : selected
    ? `${selected.title}${selected.client_name ? ` — ${selected.client_name}` : ''}`
    : 'All Jobs';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          data-testid="cc-job-filter"
          className="flex items-center gap-2 w-[200px] sm:w-[260px] h-10 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-800 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-200"
        >
          <Briefcase className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="truncate flex-1 text-left">{triggerLabel}</span>
          {value && value !== 'all' ? (
            <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-mono flex-shrink-0">
              {appCountByJob[value] || 0}
            </Badge>
          ) : (
            <span className="text-[10px] font-mono text-slate-400 flex-shrink-0">{jobs.length}</span>
          )}
          <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start" data-testid="cc-job-filter-popover">
        <Command>
          <CommandInput
            placeholder="Search jobs by title or client..."
            data-testid="cc-job-filter-search"
          />
          <CommandList className="max-h-[360px]">
            {loading ? (
              <div className="flex items-center justify-center py-6 text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                <span className="text-sm">Loading jobs…</span>
              </div>
            ) : (
              <>
                <CommandEmpty>No jobs match that search.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="all"
                    keywords={["all", "every", "everything", "*"]}
                    onSelect={() => {
                      onChange('all');
                      setOpen(false);
                    }}
                    data-testid="cc-job-filter-option-all"
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Check
                      className={`w-3.5 h-3.5 ${
                        value === 'all' ? 'text-teal-600' : 'opacity-0'
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900">All Jobs</div>
                      <div className="text-[11px] text-slate-500">Show entire pipeline</div>
                    </div>
                    <span className="text-[10px] font-mono text-slate-400">{jobs.length}</span>
                  </CommandItem>
                </CommandGroup>
                <CommandGroup heading="Open jobs">
                  {jobs.map((j) => {
                    const count = appCountByJob[j.id] || 0;
                    const hot = count >= 5;
                    return (
                      <CommandItem
                        key={j.id}
                        value={j.id}
                        keywords={[j.title || '', j.client_name || '']}
                        onSelect={() => {
                          onChange(j.id);
                          setOpen(false);
                        }}
                        data-testid={`cc-job-filter-option-${j.id}`}
                        className="flex items-center gap-2 cursor-pointer group"
                      >
                        <Check
                          className={`w-3.5 h-3.5 flex-shrink-0 ${
                            value === j.id ? 'text-teal-600' : 'opacity-0'
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-slate-900 truncate" title={j.title}>
                              {j.title}
                            </span>
                            {hot && (
                              <Flame
                                className="w-3 h-3 text-red-500 flex-shrink-0"
                                title="High activity"
                              />
                            )}
                          </div>
                          <div
                            className="flex items-center gap-1 text-[11px] text-slate-500 mt-0.5 truncate"
                            title={j.client_name || ''}
                          >
                            <Building2 className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{j.client_name || '—'}</span>
                            <span className="text-slate-400">·</span>
                            <span className="inline-flex items-center gap-0.5">
                              <User className="w-3 h-3" />
                              {count}
                            </span>
                            {j.positions ? (
                              <>
                                <span className="text-slate-400">·</span>
                                <span>{j.positions} pos</span>
                              </>
                            ) : null}
                          </div>
                        </div>
                        {onBest10 && (
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setOpen(false);
                              onBest10(j);
                            }}
                            data-testid={`cc-job-best-${j.id}`}
                            title="Find Best 10 matching candidates with AI"
                            className="inline-flex items-center gap-1 px-1.5 h-6 rounded-md bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 transition text-[10px] font-semibold flex-shrink-0"
                          >
                            <Sparkles className="w-3 h-3" />
                            <span>Best 10</span>
                          </button>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default JobsCombobox;
